import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from '../contexts/AuthContext';
import { responseDetail } from '../utils/responseDetail';
import { useApiFetch } from './useApiFetch';
import { useApiQuery } from './useApiQuery';
import type {
  CodeItem,
  CodeRule,
  PreviewBody,
  RuleChange,
  RulePreview,
  RuleSources,
} from '../types/codeRules';

export const useCodeRules = () => useApiQuery<CodeRule[]>('/code_rules/');

export const useCodeRepos = () =>
  useApiQuery<RuleSources[]>('/code_rules/repos');

export const useRepoCode = (pk: string) =>
  useApiQuery<CodeItem[]>(`/code_rules/repos/${pk}/code`);

const METHOD = { create: 'POST', update: 'PUT', delete: 'DELETE' } as const;

/**
 * Preview a rule change, then save it. The API checks admin on every call;
 * a failure shows the API's own reason (a duplicate prefix, a bad group name).
 */
export function useRuleChange(onSaved: () => void) {
  const { apiFetch } = useApiFetch();
  const { user } = useAuthContext();
  const client = useQueryClient();
  const caller = user?.sub ?? null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped by each preview and by reset(), so an older preview's reply is dropped.
  const previewId = useRef(0);

  const send = useCallback(
    async (
      url: string,
      method: string,
      body?: unknown,
      current: () => boolean = () => true
    ) => {
      setBusy(true);
      setError(null);
      // Null on a network error or abort; apiFetch does not reject.
      const res = await apiFetch(
        url,
        {
          method,
          headers:
            body === undefined
              ? undefined
              : { 'Content-Type': 'application/json' },
          body: body === undefined ? undefined : JSON.stringify(body),
        },
        { returnNonOkResponse: true }
      );
      const detail = res?.ok
        ? null
        : res
          ? await responseDetail(res)
          : 'Could not reach the API.';
      if (!current()) return null;
      setBusy(false);
      setError(detail);
      return detail === null ? res : null;
    },
    [apiFetch]
  );

  const preview = useCallback(
    async (change: RuleChange): Promise<RulePreview | null> => {
      const id = ++previewId.current;
      const current = () => id === previewId.current;
      const body: PreviewBody = {
        pk: change.action === 'create' ? null : change.pk,
        rule: change.action === 'delete' ? null : change.rule,
      };
      const res = await send('/code_rules/preview', 'POST', body, current);
      if (!res) return null;
      try {
        const result = (await res.json()) as RulePreview;
        return current() ? result : null;
      } catch {
        // The connection can drop after the headers arrive.
        if (current()) setError('Could not read the preview; try again.');
        return null;
      }
    },
    [send]
  );

  const save = useCallback(
    async (change: RuleChange) => {
      const res = await send(
        change.action === 'create'
          ? '/code_rules/'
          : `/code_rules/${change.pk}`,
        METHOD[change.action],
        change.action === 'delete' ? undefined : change.rule
      );
      if (!res) return;
      // Rules, groups and run counts all moved, and so may the sources your own
      // code groups cover; refetch them. Close now: awaiting the refetch left
      // Save clickable a second time.
      void client.invalidateQueries({
        predicate: query =>
          query.queryKey[0] === 'api' &&
          query.queryKey[1] === caller &&
          (String(query.queryKey[2]).startsWith('/code_rules') ||
            query.queryKey[2] === '/meta/code-groups'),
      });
      onSaved();
    },
    [send, client, caller, onSaved]
  );

  const reset = useCallback(() => {
    previewId.current++;
    setBusy(false);
    setError(null);
  }, []);
  return { busy, error, preview, save, reset };
}
